/**
 * Ema Client Assistant, as mounted inside the signed-in console.
 *
 * Ema is one product with two brains, chosen by context:
 *
 * - Logged out (www.systemdox.com, published KB portals) she talks to the
 *   shared chatbot backend, which is grounded in SystemDox's public marketing
 *   content and knows nothing about any particular workspace.
 * - Logged in (here) she talks to SystemDox's own AI API through
 *   `aiApi.docsAssistant`, which already carries the caller's bearer token and
 *   so can answer about the tenant's own documents.
 *
 * The face is identical either way: the same AssistBar launcher and the same
 * ChatWidget panel from @puglieseweb/shared-astro-ui. Only the transport
 * differs, which is exactly the seam ChatWidget's `transport` prop exists for.
 *
 * The console transport is RETRIEVAL-GROUNDED (ADR-030 §7 E4-4): every turn is
 * answered from passages retrieved at `scope=docs`, space-scoped to this
 * caller, and the backend declines instead of generating when it retrieves
 * nothing (shared-chatbot#145). A `grounded: false` reply is Ema saying she
 * doesn't know — render it as an ordinary answer, never as an error, and never
 * retry it through an ungrounded path.
 *
 * TWO DIFFERENT SILENCES. Retrieval reads `RepoDocChunk#` rows, which only
 * `doc_sync_worker._index_chunks` writes and only when a document's
 * contentHash changes. Every tenant synced before chunk indexing existed
 * therefore has NO chunks at deploy time, and an unqualified decline would make
 * Ema refuse every question in the workspace until each repo happens to be
 * re-synced — a regression on a shipped feature, not a limitation.
 *
 * So a decline is disambiguated before it is shown:
 *
 *   corpus not indexed yet  ->  degrade to the documents search can still see
 *   nothing relevant found  ->  the decline, unchanged
 *
 * The degrade is a DOCUMENT LIST, never a generated answer: `/docs` search
 * carries the same migration bridge for unchunked rows (doc_index
 * `_rank_by_relevance`) and enforces the same space filter, so this hands the
 * reader the old pre-ADR-030 behaviour without ever putting unretrieved text in
 * front of a model. The fail-closed rule is untouched — Ema still says nothing
 * she cannot source.
 */

import { useCallback } from 'react';
import { AssistBar } from '@puglieseweb/shared-astro-ui/components/AssistBar';
import {
  ChatWidget,
  type ChatTransport,
} from '@puglieseweb/shared-astro-ui/components/ChatWidget';
import { aiApi, type AiChatMessage, type DocsCitation } from '@/services/aiApi';
import { docsApi, type DocIndex } from '@/services/docsApi';
import { createLogger } from '@/lib/logger';

const log = createLogger('EmaAssistant');

/** Distinct from puglieseweb.com's keys — different product, different prefs. */
const FONT_SIZE_KEY = 'sdx-font-size';
const COLLAPSED_KEY = 'sdx-assistbar-collapsed';

/** Enough to be useful, few enough to stay a pointer rather than a result page. */
const FALLBACK_DOC_LIMIT = 5;

/** Render the passages an answer was grounded in as a trailing source list. */
export function appendCitations(reply: string, citations: DocsCitation[] = []): string {
  if (!citations.length) return reply;
  const lines = citations.map((c) => {
    const label = c.section ? `${c.title} — ${c.section}` : c.title;
    return `${c.n}. [${label}](${c.url})`;
  });
  return `${reply}\n\n**Sources**\n\n${lines.join('\n')}`;
}

/**
 * The reply for a workspace whose documents are not chunk-indexed yet.
 *
 * Says why she cannot answer rather than implying the documentation is silent
 * on the subject — those are different statements, and only one of them is true
 * here. The links are the fallback: the reader gets the documents, just not a
 * synthesised answer over them.
 */
export function unindexedFallbackReply(docs: DocIndex[]): string {
  const lines = docs.slice(0, FALLBACK_DOC_LIMIT).map((d) => {
    const label = d.title || d.path;
    return `- [${label}](/docs/view?id=${encodeURIComponent(d.id)})`;
  });
  return (
    "I can't read this workspace's documents yet — they finish indexing for me "
    + 'the first time each repository syncs after this release. These look '
    + 'relevant to what you asked:\n\n'
    + `${lines.join('\n')}\n\n`
    + "Once indexing has run I'll answer from the documents themselves and cite them."
  );
}

/**
 * Documents the search path can still see for a question retrieval answered
 * with nothing.
 *
 * Best effort by construction: this runs only on a decline, and a decline is
 * already a valid answer. A failure here must leave that decline standing, not
 * turn Ema's "I don't know" into "something went wrong".
 */
async function searchableDocs(question: string): Promise<DocIndex[]> {
  try {
    const { items } = await docsApi.searchDocs({ q: question, limit: FALLBACK_DOC_LIMIT });
    return items ?? [];
  } catch (err) {
    log.warn('Decline fallback search failed — showing the decline as-is', {
      error: String(err),
    });
    return [];
  }
}

export function EmaAssistant() {
  /**
   * Bridge the widget's transport contract onto the grounded docs assistant.
   *
   * aiApi.docsAssistant takes the conversation and returns
   * `{ reply, grounded, citations }`. It has no notion of sessionId,
   * suggestions or lead capture, so those come back absent — the widget treats
   * every one of them as optional and simply renders no suggestion chips. Auth
   * is already handled: aiApi has a request interceptor that attaches the
   * bearer token.
   */
  const transport = useCallback<ChatTransport>(async ({ message, history }) => {
    // The widget seeds its transcript with `greeting` as an assistant turn, so
    // history[0] is an assistant message on the very first send. Drop any
    // leading assistant turns before sending: the greeting is local UI copy and
    // carries nothing the backend needs, and the Messages API rejects a
    // conversation that does not start with a user turn.
    //
    // The grounded endpoint currently retrieves against the LATEST user turn
    // only — each answer stands on its own retrieval, so an earlier turn's
    // passages cannot smuggle context into a question that retrieved nothing.
    // History still travels for when that stops being true.
    const firstUser = history.findIndex((turn) => turn.role === 'user');
    const usable = firstUser === -1 ? [] : history.slice(firstUser);

    const messages: AiChatMessage[] = [
      ...usable.map((turn) => ({ role: turn.role, content: turn.content })),
      { role: 'user' as const, content: message },
    ];
    const response = await aiApi.docsAssistant(messages, 'docs');
    const { reply, grounded, citations } = response;
    if (grounded !== false) {
      // Citations are appended as plain markdown links rather than dropped: an
      // answer the reader cannot trace back to a document is the shape the
      // invention incident took, and every grounded answer has sources by
      // construction.
      return { reply: appendCitations(reply, citations) };
    }

    // A decline. Which of the two silences is it?
    //
    // `reason` is the backend's verdict and is authoritative: only the server
    // can tell "this tenant has no RepoDocChunk# rows at all" from "the corpus is
    // indexed and nothing matched". An earlier version guessed by re-running
    // the whole natural-language question through a substring search — which
    // required the entire lowercased sentence to appear verbatim in a document,
    // so it never matched and the fallback never fired. Every tenant was told
    // their documentation was silent, including on deploy day when nothing was
    // indexed at all.
    const reason = (response as { reason?: string }).reason;

    // Indexed and genuinely no match — the decline is the honest answer.
    // `undefined` lands here too: an older backend that sends no reason is
    // treated as the normal case rather than as "not indexed", which would
    // claim a migration is pending forever.
    if (reason !== 'not_indexed') return { reply };

    // Not indexed yet. Point at documents the search path can still see, and
    // say why there is no synthesised answer — different from claiming the
    // documentation does not cover it.
    const docs = await searchableDocs(message);
    if (!docs.length) return { reply };

    log.info('Workspace is not chunk-indexed yet — offering documents instead '
      + 'of a grounded answer', { matches: docs.length });
    return { reply: unindexedFallbackReply(docs) };
  }, []);

  return (
    <>
      <AssistBar
        avatarUrl="/ema-avatar.png"
        // Clear of the BottomNav (~3.5rem tall) so the launcher does not sit
        // on top of the quick-access bar.
        offsetBottom="4.75rem"
        fontSizeStorageKey={FONT_SIZE_KEY}
        collapsedStorageKey={COLLAPSED_KEY}
      />
      <ChatWidget
        transport={transport}
        // Inert here: this transport never sends it. The tenant is derived
        // server-side from the bearer token, which is the whole difference
        // between this path and the shared chatbot's caller-supplied tenantId
        // (shared-chatbot#141).
        tenantId="systemdox"
        businessName="Evrella"
        greeting="Hi! I'm Evrella. Ask me anything about this workspace's documentation — I answer from the documents themselves and cite them."
        avatarUrl="/ema-avatar.png"
        errorMessage="Sorry, I couldn't reach the assistant. Please try again."
        externalTrigger
        storageKey="sdx-chat"
        // Document questions, not workspace-metadata questions: retrieval
        // answers from document TEXT, so "which specs are stale?" (a property
        // of rows, not of prose) would only ever earn a decline.
        defaultSuggestions={[
          'How do I roll back a deployment?',
          'What is our authentication design?',
          'How is the data model structured?',
        ]}
      />
    </>
  );
}

export default EmaAssistant;
